import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { MerchantService } from 'src/app/services/merchant/merchant.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import Swal from 'sweetalert2';
import { environment } from 'src/environments/environment';
import { modeloNegocio, diaHabil } from '../modelo-negocio';
import { acronimoConFormato, dominioPortal, hostDelAcronimo } from 'src/app/utils/url-propia';

/**
 * Ficha de un comercio — QUIÉN ES Y SI PUEDE VENDER.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * 🔴 REDISEÑO 2026-09-20. Este componente tenía 1.307 líneas y SIETE pantallas
 * adentro, mostradas como solapas de la misma dirección. Cada una se fue a su
 * ruta propia y se llevó su estado, su carga y sus acciones:
 *
 *     Tiendas            → /console/merchants/:code/tiendas
 *     Alta de tienda     → /console/merchants/:code/tiendas/nueva
 *     Cuentas            → /console/merchants/:code/cuentas
 *     Cuentas por pagar  → (2026-10-07) sale de la ficha: va al módulo de Liquidación
 *     Clientes           → /console/merchants/:code/clientes
 *     Contactos          → /console/merchants/:code/contactos
 *     Usuarios y cajas   → /console/merchants/:code/usuarios
 *
 * Con eso se fueron de acá: el listado, el buscador, el orden y la paginación
 * de tiendas; el alta de tienda; los clientes afiliados con su sublímite; las
 * cuentas de liquidación; las cuentas por pagar con su alta, sus pagos y la
 * liquidación del período; los contactos; y los usuarios con sus dos
 * sub-solapas de personas y cajas. Los métodos del servicio y los endpoints
 * siguen exactamente donde estaban: los llama ahora cada pantalla hija.
 *
 * 📍 También se fue la maquinaria de solapas —`pestana`, `irA()`, la lista
 * `SOLAPAS` y la lectura de `?tab=…&sub=…`—: con siete rutas de verdad, el
 * navegador hace lo que hacía ese código a mano.
 * ══════════════════════════════════════════════════════════════════════════
 */
@Component({
  selector: 'app-comercios-detail',
  templateUrl: './comercios-detail.component.html',
  styleUrls: ['./comercios-detail.component.css']
})
export class ComerciosDetailComponent implements OnInit {

  codigo = '';

  /** El código del gerente en la caja (Juan, 2026-10-08): por omisión, turno no y anular sí. */
  codigoGerente: { turno: boolean; anular: boolean } | null = null;
  guardandoCodigo = false;
  comercio: any = null;
  loading = true;
  noEncontrado = false;
  revisando = false;

  /** Las tiendas vienen con la ficha (`GET /api/merchant/:code`): es el único
   *  conteo de la fila de accesos que no cuesta una llamada más. */
  tiendas: any[] = [];

  /**
   * ── EL CIRCUITO DE APROBACIÓN — DOS PASOS ────────────────────────────────
   *
   *   BORRADOR → ENVIADA → (gerente) APROBADA
   *      ▲          └── CAMBIOS_PEDIDOS ──┘
   *
   * 📍 Había un paso intermedio de analista y se sacó el 2026-08-21. `REVISADA`
   * sigue contemplado porque puede haber fichas guardadas con ese estado — se
   * muestran, no se escriben más.
   *
   * 🔴 Los dos pasitos dibujados (`kb-steps`) se fueron con el rediseño: decían
   * lo mismo que las dos insignias que estaban tres centímetros más arriba.
   * Lo que queda es UNA insignia —`estadoVenta`— que contesta la única pregunta
   * de la cabecera: ¿este comercio puede vender?
   */
  get esperaGerente(): boolean {
    return this.estadoFicha === 'ENVIADA' || this.estadoFicha === 'REVISADA';
  }
  get estadoFicha(): string { return this.comercio?.profile_status || 'BORRADOR'; }
  /** Habilitado = lo aprobaron alguna vez Y el acuerdo está vigente. */
  get habilitado(): boolean { return !!this.comercio?.approved_at && this.comercio?.state === 'ACTIVE'; }

  /**
   * LA INSIGNIA DE LA CABECERA.
   *
   * 🔴 Reemplaza a los tres avisos ámbar apilados que había —«Sigue vendiendo»,
   * «Aprobado y no vuelve a revisión» y el de los datos para liquidar—: nunca
   * aparecían los tres a la vez y apilados dejaban de advertir.
   *
   * 📍 El caso que antes necesitaba un aviso entero: un comercio al que se le
   * devolvió la ficha DESPUÉS de haber sido aprobado sigue vendiendo
   * (`approved_at` no se borra). Acá se lee solo — la insignia dice
   * «Vendiendo» y la cinta dice qué le devolvieron.
   */
  /**
   * 🔴 2026-09-24: LO QUE LE FALTA AL COMERCIO DE PARTE DE CREDITOTAL. Salió de recorrer el alta
   * de PRUEBA1: «Aprobar y habilitar» dejaba aprobar un comercio sin condición comercial y sin
   * tiendas, y quedaba «Vendiendo» sin poder vender. No bloquea el botón —aprobar la ficha es
   * una decisión aparte—: dice qué queda pendiente y lleva a la pestaña donde se resuelve.
   * Se calcula una vez por carga (ver el comentario de `cargar`).
   */
  deCrediTotal: { label: string; listo: boolean; ruta: string | null; nota?: string }[] = [];

  private calcularDeCrediTotal = (): { label: string; listo: boolean; ruta: string | null; nota?: string }[] => {
    const c = this.comercio;
    if (!c) return [];
    const tv = c.terms_vigentes;
    const rige = this.rigeDesde;
    const cuentaActiva = !!c.bank_account?.account_number
      || (c.payment_accounts || []).some((x: any) => x && x.status !== false);
    return [
      { label: 'Ficha aprobada', listo: !!c.approved_at, ruta: null,
        nota: c.approved_at ? undefined : 'con «Aprobar y habilitar», cuando el comercio la envía' },
      { label: 'Condición comercial aprobada', listo: !!(tv && tv.approved_by?.date) || !!rige, ruta: 'condiciones',
        nota: !tv ? (rige ? `aprobada, rige desde el ${rige}` : 'no tiene ninguna cargada')
          : (tv.approved_by?.date ? undefined : 'está cargada pero sin aprobar') },
      { label: 'Al menos una tienda', listo: (this.tiendas || []).length > 0, ruta: 'tiendas',
        nota: (this.tiendas || []).length ? undefined : 'sin tienda no puede vender' },
      { label: 'Cuenta para liquidarle', listo: cuentaActiva, ruta: 'cuentas',
        nota: cuentaActiva ? undefined : 'sin cuenta no se le puede pagar' },
    ];
  };

  get faltaDeCrediTotal(): boolean { return this.deCrediTotal.some(x => !x.listo); }

  /** Lo que pide la ficha para poder enviarse, tal cual lo calcula el núcleo. Sólo sin aprobar. */
  avance: { completa: boolean; detalle: { id: string; label: string; listo: boolean }[] } | null = null;

  estadoVenta: { texto: string; clase: string; icono: string } =
    { texto: '', clase: 'kb-badge-neutro', icono: 'fa-minus-circle' };

  private calcularEstadoVenta = (): { texto: string; clase: string; icono: string } => {
    if (this.comercio?.state === 'TERMINATED')
      return { texto: 'Relación terminada', clase: 'kb-badge-peligro', icono: 'fa-ban' };
    if (this.comercio?.state === 'SUSPENDED')
      return { texto: 'Suspendido', clase: 'kb-badge-alerta', icono: 'fa-pause-circle' };
    // 2026-09-24: aprobado y activo pero SIN TIENDAS no vende —la compra se pide en una tienda—.
    // Decía «Vendiendo» (caso PRUEBA1, recién aprobado). La misma regla está en la lista.
    if (this.habilitado && !(this.tiendas || []).length)
      return { texto: 'Sin tiendas', clase: 'kb-badge-alerta', icono: 'fa-store-slash' };
    if (this.habilitado)
      return { texto: 'Vendiendo', clase: 'kb-badge-exito', icono: 'fa-check-circle' };
    if (this.esperaGerente)
      return { texto: 'Ficha en revisión', clase: 'kb-badge-alerta', icono: 'fa-hourglass-half' };
    if (this.estadoFicha === 'CAMBIOS_PEDIDOS')
      return { texto: 'Ficha devuelta', clase: 'kb-badge-peligro', icono: 'fa-undo' };
    return { texto: 'Todavía no vende', clase: 'kb-badge-neutro', icono: 'fa-minus-circle' };
  };

  /**
   * LA CINTA — UNA SOLA, Y EN ESTE ORDEN.
   *
   * 🔴 La regla de la casa desde el rediseño: una cinta por pantalla. Acá
   * llegaron a mostrarse tres a la vez, y tres avisos ámbar seguidos dejan de
   * advertir. El orden es por lo que bloquea más: primero lo que impide vender,
   * después lo que impide facturar o liquidar.
   *
   * 📍 La cinta NO repite el estado —eso ya lo dice la insignia—: dice la
   * consecuencia, que es lo que la insignia no puede decir.
   */
  aviso: { tono: 'alerta' | 'info'; texto: string } | null = null;

  private calcularAviso = (): { tono: 'alerta' | 'info'; texto: string } | null => {
    const c = this.comercio;
    if (!c) return null;

    if (c.state === 'TERMINATED') {
      return { tono: 'alerta', texto: 'No puede vender más. La cartera que ya colocó se sigue '
        + 'cobrando y liquidando hasta que se agote.'
        + (c.state_reason ? ` Motivo: ${c.state_reason}` : '') };
    }
    if (c.state === 'SUSPENDED') {
      return { tono: 'alerta', texto: 'Dejó de poder vender, pero su cartera sigue viva y se le '
        + 'sigue liquidando: suspender no es dar de baja.'
        + (c.state_reason ? ` Motivo: ${c.state_reason}` : '') };
    }
    if (this.estadoFicha === 'CAMBIOS_PEDIDOS') {
      // Sin el texto, «cambios pedidos» es un cartel que no dice nada y el
      // comercio vuelve a llamar por teléfono.
      return { tono: 'alerta', texto: 'Se le devolvió la ficha para corregir. '
        + (c.profile_feedback || 'No quedó escrito qué hay que corregir.') };
    }
    if (this.esperaGerente) {
      return { tono: 'alerta', texto: 'La ficha está esperando el visto bueno del gerente, y solo '
        + 'entonces se le puede vender.'
        + (this._auth.hasPermission('config.comercios.aprobar')
            ? '' : ' Tu usuario no tiene el permiso para aprobar comercios.') };
    }
    if (this.estadoFicha === 'BORRADOR') {
      return { tono: 'info', texto: 'El comercio todavía no envió su ficha a revisar. Hasta que la '
        + 'envíe y el gerente la apruebe, no se le puede vender.' };
    }
    if (this.sinComision && this.rigeDesde && !this.sinCuenta) {
      return { tono: 'info', texto: `Sus condiciones ya están aprobadas y rigen desde el ${this.rigeDesde}: `
        + 'desde ese día puede vender y se le factura.' };
    }
    if (this.sinComision && this.sinCuenta) {
      return { tono: 'alerta', texto: 'Le falta la comisión vigente y la cuenta bancaria: sin la '
        + 'primera no se le puede facturar y sin la segunda no se le puede liquidar.' };
    }
    if (this.sinComision) {
      return { tono: 'alerta', texto: 'No tiene comisión vigente, así que no se le puede facturar. '
        + 'Las condiciones se versionan: hay que crear una versión nueva y aprobarla.' };
    }
    if (this.sinCuenta) {
      return { tono: 'alerta', texto: 'No tiene cuenta bancaria activa, así que no se le puede '
        + 'liquidar.' };
    }
    return null;
  };

  /** Los adjuntos, en palabras. Un `kind` crudo no le dice nada a nadie. */
  /**
   * Los documentos de la ficha, uno por renglón (2026-09-24). Son los mismos tipos y la
   * regla de obligatoriedad del NÚCLEO (`services/merchant_perfil.js`): hoy ningún adjunto frena el envío.
   * sólo el RIF es obligatorio, así que sólo su falta va en ámbar; los demás, si no
   * están, dicen «Sin cargar» en gris — no es un error, es un dato.
   */
  readonly DOCUMENTOS = [
    { kind: 'RIF',                label: 'RIF',                      obligatorio: false }, // 2026-09-24: el núcleo no exige adjuntos (MC-086 abierta)
    { kind: 'REGISTRO_MERCANTIL', label: 'Registro mercantil',       obligatorio: false },
    { kind: 'CEDULA_REP',         label: 'Cédula del representante', obligatorio: false },
    { kind: 'LOGO',               label: 'Logo',                     obligatorio: false },
  ];

  documentosDe = (kind: string): any[] => (this.comercio?.documents || []).filter((d: any) => d.kind === kind);

  /** Los que no son de un tipo conocido van al final como «Otro documento». */
  get otrosDocumentos(): any[] {
    const conocidos = this.DOCUMENTOS.map(d => d.kind);
    return (this.comercio?.documents || []).filter((d: any) => !conocidos.includes(d.kind));
  }

  readonly ADJUNTOS: any = {
    RIF: 'RIF',
    REGISTRO_MERCANTIL: 'Registro mercantil',
    CEDULA_REP: 'Cédula del representante',
    LOGO: 'Logo',
    OTRO: 'Otro'
  };

  /**
   * El rastro, en palabras.
   *
   * 🔴 SIN EL ACTOR. Antes decía «La aprobó el gerente», y no era cierto que se
   * supiera: `profile_history[].by` es un identificador de usuario que el
   * detalle no puebla (`controllers/merchant.js:57` sólo popula `organization`).
   * Nombrar un cargo que no se leyó de ningún lado es inventar el dato.
   */
  readonly ACCIONES: any = {
    ENVIAR: 'La envió el comercio',
    REVISAR_OK: 'Revisada',
    APROBAR: 'Aprobada',
    PEDIR_CAMBIOS: 'Devuelta para corregir'
  };

  get historial(): any[] { return this.comercio?.profile_history || []; }

  /** Con el almacenamiento local el núcleo guarda la ruta sola (`/uploads/…`): se sirve desde el núcleo. */
  urlArchivo(url: string): string { return url && url.startsWith('/') ? `${environment.BASE_LA}${url}` : url; }

  constructor(
    private ruta: ActivatedRoute,
    private router: Router,
    private _merchant: MerchantService,
    public _auth: AuthService
  ) { }

  ngOnInit(): void {
    this.ruta.paramMap.subscribe(p => {
      this.codigo = p.get('code') || '';
      this.cargar();
    });

    // 🔴 Se mira el árbol de rutas, no la cadena de la dirección: si esta ruta
    // tiene una hija activa, estamos en una pestaña; si no, en la ficha. Y se
    // vuelve a mirar en cada navegación, porque cambiar de pestaña NO vuelve a
    // construir este componente — ése es justamente el punto de anidarlas.
    const mirar = () => { this.enLaFicha = !this.ruta.firstChild; };
    mirar();
    this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(mirar);
  }

  puede = (accion: string): boolean => this._auth.hasPermission(`config.comercios.${accion}`);

  /**
   * ¿Estamos en la ficha misma, o en una de sus seis hijas?
   *
   * 🔴 Desde el 2026-09-20 las seis pantallas de adentro son RUTAS HIJAS de
   * ésta (pedido de Juan: *«cámbiame la información que está abajo, en vez de
   * pasarme por otro componente»*). El encabezado, la tira del portal y la barra
   * de pestañas viven acá y no se repintan; lo de abajo entra por el
   * `<router-outlet>`.
   *
   * Y por eso hace falta esta bandera: las tres tarjetas de la ficha —
   * Identificación, Condición vigente y Rastro— son el contenido de la pestaña
   * «Ficha», o sea de la ruta VACÍA. Sin esto se dibujarían también debajo de
   * Tiendas o de Clientes, que es justo lo que la pestaña vino a evitar.
   *
   * Se calcula del árbol de rutas y no de la cadena de la dirección: comparar
   * texto se rompe con una barra de más, con un parámetro de consulta o el día
   * que el código de un comercio se llame «tiendas».
   */
  enLaFicha = true;

  // ══ EJE-01 (Mario, 14-oct): el ejecutivo de CrediTotal del comercio ══
  ejecutivo: any = null;
  usuarioEjecutivo = '';
  mediosEjecutivo = (): string => [this.ejecutivo?.contacto?.telefono, this.ejecutivo?.contacto?.correo].filter(Boolean).join(' · ') || 'sin teléfono ni correo en su usuario';
  guardandoEjecutivo = false;
  errorEjecutivo = '';
  asignarEjecutivo = async (usuario: string | null): Promise<void> => {
    this.guardandoEjecutivo = true; this.errorEjecutivo = '';
    try { this.ejecutivo = await this._merchant.setEjecutivo(this.codigo, usuario ? usuario.trim() : null); this.usuarioEjecutivo = ''; }
    catch (e: any) { this.errorEjecutivo = e?.error?.msg || 'No se pudo asignar.'; } finally { this.guardandoEjecutivo = false; }
  };

  cargar = async (): Promise<void> => {
    this.loading = true;
    this.noEncontrado = false;
    try {
      const rep = await this._merchant.getMerchant(this.codigo);
      // EJE-01: su ejecutivo de CrediTotal (lo que ve el comercio en «¿con quién hablo?»).
      this._merchant.getEjecutivo(this.codigo).then((r: any) => { this.ejecutivo = r || null; }).catch(() => { this.ejecutivo = null; });
      this._merchant.getCodigoGerente(this.codigo)
        .then((r: any) => this.codigoGerente = r?.codigo_gerente || { turno: false, anular: true })
        .catch(() => this.codigoGerente = { turno: false, anular: true });
      this.comercio = rep?.merchant || null;
      this.tiendas = rep?.shops || [];
      this.noEncontrado = !this.comercio;
    } catch {
      this.comercio = null;
      this.noEncontrado = true;
    } finally {
      // 🔴 La insignia y la cinta se calculan UNA VEZ por carga, no en un
      // getter. Un getter que devuelve un objeto nuevo en cada ciclo de
      // deteccion de cambios rompe el `*ngIf` que lo lee: Angular compara por
      // referencia en su segunda pasada, ve un objeto distinto y tira
      // ExpressionChangedAfterItHasBeenChecked. Y todo lo que leen —estado,
      // ficha, comision, cuenta— sale de esta misma respuesta.
      this.estadoVenta = this.calcularEstadoVenta();
      this.aviso = this.calcularAviso();
      this.deCrediTotal = this.calcularDeCrediTotal();
      this.loading = false;
    }
    // 🔴 2026-09-24: mientras el comercio no está aprobado, la consola muestra LA MISMA lista de
    // requisitos que ve el comercio en su portal (`GET /:code/perfil` → `avance`, calculada por
    // el núcleo). Antes la consola decía «el RIF es el único obligatorio» y el portal pedía RIF y
    // cédula, mientras el núcleo no exigía ninguno: tres verdades. Ahora hay una.
    this.avance = null;
    if (this.comercio && !this.comercio.approved_at) {
      this._merchant.getPerfil(this.codigo)
        .then((r: any) => { this.avance = r?.avance || null; })
        .catch(() => { this.avance = null; });
    }
    // La ficha ya está en pantalla; los conteos entran después. Ver `cargarConteos`.
    // 🔴 2026-09-24, Juan: las pestañas llevan sólo el nombre («que cuando entre vea las
    // opciones»). Sin conteos a la vista no se piden: eran tres consultas por cada apertura.
    // `cargarConteos` queda por si vuelven.
  };

  // ══ LOS CONTEOS DE LA FILA DE ACCESOS ═════════════════════════════════════
  //
  // 🔴 SE PIDEN DESPUÉS DE PINTAR, NO ANTES, y las tres juntas.
  //
  // La primera versión de esta ficha los dejó sin número y puso la palabra «Ver»
  // en tres de las siete celdas, para ahorrarse tres llamadas. Juan lo vio y
  // tenía razón: *«te fuiste solo a lo minimalista, no tiene detalle»*. Una fila
  // de accesos donde tres celdas no dicen nada no informa — y el conteo ES el
  // dato por el que se entra: si le debo plata a este comercio, si tiene
  // clientes, si alguien puede entrar a su portal.
  //
  // Se resuelve sin pagar el precio que se evitó: van FUERA de `cargar()`, así
  // que no retrasan el primer pintado, y van las tres en paralelo, así que
  // cuestan la más lenta y no la suma. Mientras llegan, la celda muestra su
  // esqueleto; si una falla, queda en `null` y la celda dice «Ver» — que es
  // «no se pudo contar», no «no hay».
  //
  // ⚠️ La diferencia con lo que se sacó de Organización no es el número de
  // llamadas: allá se pedían TODOS los comercios SIN PAGINAR para mostrar tres
  // números que ya estaban en otra pantalla. Acá se piden tres conteos que no
  // están en ningún otro lado, y se piden paginados de a uno.
  conteos: { clientes: number | null; porPagar: number | null; usuarios: number | null } = {
    clientes: null, porPagar: null, usuarios: null,
  };
  cargandoConteos = false;

  cargarConteos = async (): Promise<void> => {
    this.cargandoConteos = true;
    const [cli, pag, usr] = await Promise.all([
      // `porPagina: 1` porque lo único que se quiere es el TOTAL que trae la
      // respuesta paginada: traer la página entera para contarla sería el mismo
      // defecto de Organización con otro disfraz.
      this._merchant.getMerchantCustomers(this.codigo, { pagina: 1, porPagina: 1 }).catch(() => null),
      this._merchant.getPayables(this.codigo, {}).catch(() => null),
      this._merchant.getMerchantUsers(this.codigo).catch(() => null),
    ]);

    const total = (r: any, ...campos: string[]): number | null => {
      if (!r) { return null; }
      for (const c of campos) { if (typeof r[c] === 'number') { return r[c]; } }
      for (const c of campos) { if (Array.isArray(r[c])) { return r[c].length; } }
      return null;
    };

    this.conteos = {
      clientes: total(cli, 'total', 'count', 'customers'),
      // Del resumen de por pagar interesa cuántos documentos hay abiertos, que es
      // la razón por la que alguien entra ahí. El monto NO se muestra acá: el
      // servidor lo suma mezclando monedas (defecto anotado el 2026-09-20), y un
      // total que mezcla bolívares con dólares no se pone en una ficha.
      porPagar: total(pag, 'total', 'count') ?? total(pag?.totales, 'documentos'),
      usuarios: total(usr, 'total', 'count', 'users', 'usuarios'),
    };
    this.cargandoConteos = false;
  };

  volver = (): void => { this.router.navigate(['/console/merchants']); };

  editar = (): void => {
    this.router.navigate(['/console/merchants', this.codigo, 'editar']);
  };

  // ══ EL CIRCUITO DE APROBACIÓN ═════════════════════════════════════════════

  /** PASO 2 — el gerente: el visto bueno que HABILITA al comercio. */
  aprobarFicha = async (): Promise<void> => {
    // 🔴 2026-09-24: decía «a partir de aquí se le puede vender» aunque le faltaran la condición
    // y las tiendas (caso PRUEBA1). Ahora dice lo que quedaría pendiente de parte de CrediTotal.
    const pendientes = this.deCrediTotal.filter(x => !x.listo && x.label !== 'Ficha aprobada').map(x => x.label.toLowerCase());
    const r = await Swal.fire({
      icon: 'warning',
      title: '¿Aprobar y habilitar el comercio?',
      html: pendientes.length
        ? `Queda aprobado, pero <b>todavía no va a poder vender</b>: falta ${pendientes.join(', ')}.`
        : 'A partir de aquí <b>se le puede vender con este comercio</b>.<br>Es la decisión que lo pone a operar.',
      showCancelButton: true, confirmButtonText: 'Aprobar y habilitar', cancelButtonText: 'Cancelar'
    });
    if (!r.isConfirmed) return;
    await this.mandar(() => this._merchant.aprobarFicha(this.codigo, true),
                      'Comercio aprobado',
                      pendientes.length ? `Para que pueda vender falta: ${pendientes.join(', ')}.` : 'Ya se le puede vender.');
  };

  /**
   * Devolver la ficha.
   *
   * 🔴 El motivo es OBLIGATORIO y el back también lo exige. Devolver una ficha
   * sin decir qué corregir obliga al comercio a adivinar y termina en una
   * llamada por teléfono — que es justo lo que este circuito viene a evitar.
   */
  pedirCambios = async (): Promise<void> => {
    const r = await Swal.fire({
      icon: 'question', title: '¿Qué hay que corregir?',
      input: 'textarea',
      inputPlaceholder: 'Ej.: el RIF adjunto está vencido y el domicilio no coincide con el registro.',
      inputValidator: (v) => (v && v.trim().length >= 10)
        ? null : 'Escribe qué corregir: el comercio va a leer esto tal cual.',
      showCancelButton: true, confirmButtonText: 'Devolver la ficha', cancelButtonText: 'Cancelar'
    });
    if (!r.isConfirmed) return;
    const obs = String(r.value).trim();
    await this.mandar(() => this._merchant.aprobarFicha(this.codigo, false, obs),
                      'Ficha devuelta', 'El comercio ya puede ver qué corregir.');
  };

  /** El envoltorio común: bloquea, llama, avisa y recarga. */
  private mandar = async (fn: () => Promise<any>, titulo: string, texto: string): Promise<void> => {
    if (this.revisando) return;
    this.revisando = true;
    try {
      await fn();
      await Swal.fire({ icon: 'success', title: titulo, text: texto, timer: 2200, showConfirmButton: false });
      await this.cargar();   // el estado cambió: hay que releerlo
    } catch (e: any) {
      // Un error dice QUÉ se intentaba: «No se pudo» solo no ubica a nadie.
      Swal.fire({ icon: 'error', title: 'No se pudo revisar la ficha',
                  text: e?.error?.msg || e?.message || 'Intenta de nuevo.',
                  footer: e?.error?.detalle || undefined });
    } finally {
      this.revisando = false;
    }
  };

  // ══ ESTADO DEL ACUERDO ════════════════════════════════════════════════════

  /**
   * 🔴 Suspender NO es dar de baja: el comercio deja de vender, pero su cartera
   * sigue viva y se le sigue liquidando. Por eso pide motivo y lo dice en la
   * confirmación — quien lo hace tiene que saber qué está haciendo.
   */
  cambiarEstado = async (estado: 'ACTIVE' | 'SUSPENDED' | 'TERMINATED'): Promise<void> => {
    const textos: any = {
      SUSPENDED: {
        title: `¿Suspender ${this.comercio.name}?`,
        html: 'Deja de poder vender. <b>Su cartera sigue viva y se le sigue liquidando:</b> ' +
              'suspender no es dar de baja.',
        confirmButtonText: 'Suspender', pideMotivo: true
      },
      TERMINATED: {
        title: `¿Terminar la relación con ${this.comercio.name}?`,
        html: 'No podrá vender más. <b>La cartera que ya colocó se sigue cobrando y liquidando</b> ' +
              'hasta que se agote.',
        confirmButtonText: 'Terminar', pideMotivo: true
      },
      ACTIVE: {
        title: `¿Reactivar ${this.comercio.name}?`,
        html: 'Vuelve a poder vender con sus condiciones comerciales vigentes.',
        confirmButtonText: 'Reactivar', pideMotivo: false
      }
    };
    const t = textos[estado];

    const r = await Swal.fire({
      title: t.title, html: t.html, icon: 'question',
      input: t.pideMotivo ? 'textarea' : undefined,
      inputLabel: t.pideMotivo ? 'Motivo (queda registrado)' : undefined,
      inputValidator: t.pideMotivo
        ? (v: string) => (!v || !v.trim() ? 'El motivo es obligatorio' : null) as any
        : undefined,
      showCancelButton: true, confirmButtonText: t.confirmButtonText, cancelButtonText: 'Cancelar'
    });
    if (!r.isConfirmed) return;

    try {
      await this._merchant.setState(this.codigo, estado, (r.value || '').trim());
      await this.cargar();
      Swal.fire({ icon: 'success', title: 'Estado actualizado', timer: 1600, showConfirmButton: false });
    } catch (e: any) {
      Swal.fire({ icon: 'error', title: 'No se pudo cambiar el estado del comercio',
                  text: e?.error?.msg || e?.message || 'Intenta de nuevo.' });
    }
  };

  // ══ QUIÉN FIRMA POR EL COMERCIO ═══════════════════════════════════════════
  //
  // 🔴 El dato estaba guardado desde que existe la ficha que completa el propio
  // comercio (`legal_rep` en `models/merchant.js`: nombre, apellido, cédula,
  // cargo, teléfono y correo) y NO SE MOSTRABA EN NINGUNA de las siete
  // pantallas. Quien tenía que mandar a firmar un contrato lo pedía por
  // teléfono igual que antes de que el campo existiera.

  get representante(): string {
    const r = this.comercio?.legal_rep;
    return [r?.name, r?.lastname].filter(Boolean).join(' ').trim();
  }

  /** La cédula y el cargo van pegados al nombre, no en tres renglones sueltos. */
  get representanteDetalle(): string {
    const r = this.comercio?.legal_rep;
    return [r?.identity_card ? `C.I. ${r.identity_card}` : '', r?.position || '']
      .filter(Boolean).join(' · ');
  }

  // ══ LA CONDICIÓN VIGENTE ══════════════════════════════════════════════════

  get sinCuenta(): boolean { return !this.comercio?.bank_account?.account_number; }

  /**
   * EL TARIFARIO MANDA SOBRE LA COMISIÓN SUELTA.
   *
   * 🔴 Acá se leía `commission.value` y nada más, y eso muestra 0 % en todo
   * acuerdo cargado con tarifario: desde que existe `settlement.rates`
   * (`models/merchant_terms.js`), la comisión vive EN LA FILA DEL PLAZO, porque
   * cobrar a 24 horas cuesta más que cobrar a 15 días y es el mismo acuerdo.
   * Un 0 % en una ficha se lee como «no le cobramos nada», que es lo contrario.
   *
   * 📍 El criterio es el mismo de la pantalla de condiciones (plazos activos,
   * el marcado por omisión o el más rápido): dos pantallas que muestran el
   * mismo acuerdo no pueden usar dos reglas distintas.
   */
  private plazoPorOmision = (): any => {
    const activos = (this.comercio?.terms_vigentes?.settlement?.rates || [])
      .filter((r: any) => (r?.days || r?.term) && r.status !== false)
      .map((r: any) => ({
        ...r,
        days: Number(r.days) || ({ '24H': 1, '7D': 7, '15D': 15, '30D': 30 } as any)[r.term] || 7
      }))
      .sort((a: any, b: any) => a.days - b.days);
    return activos.find((r: any) => r.is_default) || activos[0] || null;
  };

  /**
   * 🔴 2026-09-25: LOS OTROS PLAZOS DEL TARIFARIO. La ficha decía «3%» y nada más, y el acuerdo de
   * MundoTotal tiene tres: 5 % a 24 horas, 3 % a 7 días (el de omisión) y 2 % a 15 días. Quien mira
   * la ficha leía una comisión fija donde hay una que depende de cuándo cobra el comercio.
   */
  otrosPlazos = (): string => {
    const omision = this.plazoPorOmision();
    const dias = (r: any) => Number(r.days) || ({ '24H': 1, '7D': 7, '15D': 15, '30D': 30 } as any)[r.term] || 7;
    return (this.comercio?.terms_vigentes?.settlement?.rates || [])
      .filter((r: any) => (r?.days || r?.term) && r.status !== false && r !== omision && r._id !== omision?._id)
      .sort((a: any, b: any) => dias(a) - dias(b))
      .map((r: any) => `${r.type_calc === 'FIXED' ? `$${r.commission_value}` : `${r.commission_value}%`} a ${dias(r) === 1 ? '24 horas' : `${dias(r)} días`}`)
      .join(' · ');
  };

  /** La condición aprobada que todavía no rige (fecha futura), como dd/mm/aaaa; o null. */
  get rigeDesde(): string | null {
    const d = this.comercio?.terms_proxima?.version?.date_init;
    return d ? new Date(d).toLocaleDateString('es-VE', { timeZone: 'America/Caracas', day: '2-digit', month: '2-digit', year: 'numeric' }) : null;
  }

  get sinComision(): boolean {
    if (this.plazoPorOmision()) return false;
    const c = this.comercio?.terms_vigentes?.commission;
    return !c || c.value === null || c.value === undefined;
  }

  comision = (): string => {
    const p = this.plazoPorOmision();
    if (p) return p.type_calc === 'FIXED' ? `$${p.commission_value}` : `${p.commission_value}%`;
    const c = this.comercio?.terms_vigentes?.commission;
    if (!c || c.value === null || c.value === undefined) return 'sin definir';
    return c.type_calc === 'PERCENTAGE' ? `${c.value}%` : `$${c.value}`;
  };

  /**
   * El modelo de riesgo del acuerdo vigente: quién se come la mora.
   *
   * 🔴 VOLVIÓ. Estaba en la ficha vieja y se cayó con el recorte del 2026-09-20 —
   * Juan lo notó de una: *«la vista anterior estaba más bonita, no tiene detalle»*.
   * Y no es un adorno: dice si el comercio responde por el crédito que vendió o
   * si el riesgo es de CrediTotal, que es la mitad de lo que define un acuerdo.
   *
   * ⚠️ La traducción es LA MISMA que usa la pantalla de condiciones
   * (`comercios-condiciones.component.ts:94`), palabra por palabra. Dos pantallas
   * que traduzcan el mismo código de forma distinta hacen dudar de cuál miente.
   */
  /** 1.6b: el modelo y la regla de día hábil de la versión que rige hoy. */
  modelo = (): string => modeloNegocio(this.comercio?.terms_vigentes);
  diaHabil = (): string => diaHabil(this.comercio?.terms_vigentes);

  riesgo = (): string => {
    const r = this.comercio?.terms_vigentes?.risk_model;
    const t = ({ NO_RECOURSE: 'Sin recurso', RECOURSE: 'Con recurso', MIXED: 'Mixto' } as any)[r?.type_risk];
    if (!t) { return '—'; }
    if (r.type_risk === 'RECOURSE' && r.recourse_days) { return `${t} · ${r.recourse_days} días`; }
    if (r.type_risk === 'MIXED' && r.guarantee_fund_pct) { return `${t} · fondo ${r.guarantee_fund_pct} %`; }
    return t;
  };

  /** Un porcentaje sin base no significa nada: la base viaja pegada. */
  baseComision = (): string => {
    const base = this.plazoPorOmision()?.base
              || this.comercio?.terms_vigentes?.commission?.base
              || 'FINANCED';
    return ({
      SALE:      'sobre el monto vendido',
      FINANCED:  'sobre el saldo a financiar',
      COLLECTED: 'sobre lo cobrado'
    } as any)[base] || 'sobre el saldo a financiar';
  };

  /** El plazo ES la frecuencia de liquidación: cuándo cobra el comercio. */
  plazoCobro = (): string => {
    const p = this.plazoPorOmision();
    if (p) return (p.label || '').trim() || (Number(p.days) === 1 ? '24 horas' : `${p.days} días`);
    // 📍 Legado: las condiciones anteriores al tarifario traen una frecuencia.
    const f = this.comercio?.terms_vigentes?.settlement?.frequency;
    return ({ DAILY: 'cada 24 horas', WEEKLY: 'cada 7 días',
              BIWEEKLY: 'cada 15 días', MONTHLY: 'cada mes' } as any)[f] || '—';
  };

  get vigenteDesde(): any { return this.comercio?.terms_vigentes?.version?.date_init || null; }

  get nombreVersion(): string {
    const v = this.comercio?.terms_vigentes?.version;
    if (!v) return '';
    return [v.name, v.number ? `versión ${v.number}` : ''].filter(Boolean).join(' · ');
  }

  // ══ LA DIRECCIÓN DEL PORTAL ═══════════════════════════════════════════════
  //
  // 🔴 Estaba en una caja propia de 98 px de alto que mostraba la dirección
  // escrita. Nadie la lee: se abre o se copia. Quedaron los dos botones de la
  // cabecera, que es lo que se usaba de esa caja.

  /**
   * 📍 El portal es uno solo para todos los comercios, pero cada uno recibe una
   * dirección que ya trae su código adentro (`…/c/MT`). Así su gente entra
   * escribiendo sólo su usuario, sin tener que saber ni dictar el código del
   * comercio — que es el dato que más se equivoca por teléfono.
   *
   * La base sale de la configuración de entorno (`BASE_PORTAL_COMERCIOS`): no
   * se escribe acá, porque cada ambiente tiene su portal.
   */
  get urlPortal(): string {
    // URL propia: con la dirección LISTA el portal se abre por su propio acrónimo; si no (apagada, en
    // preparación o con error) se cae a `/c/<código>/login` como siempre.
    const p = this.comercio?.portal;
    if (p?.acronimo && p.estado_direccion === 'LISTA') return `https://${hostDelAcronimo(p.acronimo)}`;
    const base = String(environment.BASE_PORTAL_COMERCIOS || '').replace(/\/+$/, '');
    return `${base}/c/${(this.codigo || '').toUpperCase()}/login`;
  }

  /**
   * ⚠️ `navigator.clipboard` NO existe fuera de un contexto seguro: si la
   * consola se abre por IP en http —que es como la abren en las tiendas— es
   * `undefined` y el botón no haría absolutamente nada, sin error. De ahí el
   * respaldo con el textarea.
   */
  private alPortapapeles = async (texto: string): Promise<boolean> => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(texto);
        return true;
      }
    } catch { /* sigue por el respaldo */ }
    try {
      const ta = document.createElement('textarea');
      ta.value = texto;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch { return false; }
  };

  copiar = async (texto: string, aviso: string): Promise<void> => {
    const ok = await this.alPortapapeles(texto);
    Swal.fire({
      toast: true, position: 'top-end', timer: 1600, showConfirmButton: false,
      icon: ok ? 'success' : 'error',
      title: ok ? aviso : 'No se pudo copiar la dirección'
    });
  };

  cambiarCodigoGerente = async (clave: 'turno' | 'anular'): Promise<void> => {
    if (!this.codigoGerente || this.guardandoCodigo) return;
    this.guardandoCodigo = true;
    try {
      const r = await this._merchant.setCodigoGerente(this.codigo, { [clave]: !this.codigoGerente[clave] });
      this.codigoGerente = r?.codigo_gerente || this.codigoGerente;
    } catch {
      Swal.fire({ icon: 'error', title: 'No se pudo guardar', text: 'Intenta de nuevo.' });
    } finally {
      this.guardandoCodigo = false;
    }
  };

  // ── URL propia: la tarjeta «Dirección del portal» ───────────────────────────────────────────

  dominio = dominioPortal();
  hostDe = hostDelAcronimo;
  probandoDireccion = false;
  cambioAbierto = false;
  nuevoAcronimo = '';
  cambiandoAcronimo = false;
  estadoNuevo: { fase: 'vacio' | 'revisando' | 'ok' | 'mal'; mensaje: string } = { fase: 'vacio', mensaje: '' };
  habilitando = false;
  habilitarAbierto = false;
  errorHabilitar = '';
  private timerNuevo: any = null;
  private pedidoNuevo = 0;

  get chipDireccion(): { texto: string; clase: string } {
    switch (this.comercio?.portal?.estado_direccion) {
      case 'LISTA': return { texto: 'Lista', clase: 'kb-badge-exito' };
      case 'EN_PREPARACION': return { texto: 'En preparación', clase: 'kb-badge-alerta' };
      case 'ERROR': return { texto: 'Con error', clase: 'kb-badge-peligro' };
      case 'BORRADA': return { texto: 'Borrada', clase: 'kb-badge-neutro' };
      default: return { texto: 'Sin crear', clase: 'kb-badge-neutro' };
    }
  }

  /** La dirección anterior que todavía redirige (la de fecha más lejana vigente), o null. */
  get anteriorVigente(): { acronimo: string; redirige_hasta: string } | null {
    const ahora = Date.now();
    const v = (this.comercio?.portal?.acronimos_anteriores || [])
      .filter((x: any) => x?.acronimo && x.redirige_hasta && new Date(x.redirige_hasta).getTime() > ahora)
      .sort((a: any, b: any) => new Date(b.redirige_hasta).getTime() - new Date(a.redirige_hasta).getTime());
    return v[0] || null;
  }

  probarDireccion = async (): Promise<void> => {
    if (this.probandoDireccion) return;
    this.probandoDireccion = true;
    try {
      const r = await this._merchant.revisarDireccion(this.codigo);
      if (r?.portal && this.comercio) this.comercio = { ...this.comercio, portal: r.portal };
    } catch {
      Swal.fire({ icon: 'error', title: 'No se pudo revisar la dirección', text: 'Intenta de nuevo.' });
    } finally {
      this.probandoDireccion = false;
    }
  };

  /** «Habilitar dirección propia»: sin acrónimo, o con la dirección sin crear, con error o borrada (lo mismo que acepta el núcleo). */
  get puedeHabilitarDireccion(): boolean {
    const p = this.comercio?.portal;
    return !p?.acronimo || !p.estado_direccion || ['SIN_CREAR', 'ERROR', 'BORRADA'].includes(p.estado_direccion);
  }

  private _avisoHabilitada(r: any): void {
    if (r?.portal && this.comercio) this.comercio = { ...this.comercio, portal: r.portal };
    this.habilitarAbierto = false;
    if (r?.apagado) {
      Swal.fire({ icon: 'info', title: 'Acrónimo guardado',
        text: 'Acrónimo guardado. La dirección se crea cuando se prenda la URL propia en este ambiente.' });
    } else {
      const est = r?.portal?.estado_direccion;
      Swal.fire({ toast: true, position: 'top-end', timer: 2600, showConfirmButton: false,
        icon: est === 'ERROR' ? 'error' : 'success',
        title: est === 'LISTA' ? 'Dirección lista' : est === 'ERROR' ? 'La dirección quedó con error' : 'Dirección en preparación' });
    }
  }

  habilitarDireccion = async (): Promise<void> => {
    if (this.habilitando) return;
    this.habilitando = true;
    try {
      const r = await this._merchant.habilitarDireccionPropia(this.codigo);
      this._avisoHabilitada(r);
      await this.cargar();
    } catch (e: any) {
      if (e?.error?.code === 'FALTA_ACRONIMO') {
        this.nuevoAcronimo = String(e.error.sugerido || '').toLowerCase();
        this.errorHabilitar = '';
        this.estadoNuevo = { fase: 'vacio', mensaje: '' };
        this.habilitarAbierto = true;
        if (this.nuevoAcronimo) this.revisarNuevoAcronimo();
      } else {
        Swal.fire({ icon: 'error', title: 'No se pudo habilitar la dirección', text: e?.error?.msg || 'Intenta de nuevo.' });
      }
    } finally {
      this.habilitando = false;
    }
  };

  confirmarHabilitar = async (): Promise<void> => {
    if (this.estadoNuevo.fase !== 'ok' || this.habilitando) return;
    this.habilitando = true; this.errorHabilitar = '';
    try {
      const r = await this._merchant.habilitarDireccionPropia(this.codigo, this.nuevoAcronimo);
      this._avisoHabilitada(r);
      await this.cargar();
    } catch (e: any) {
      this.errorHabilitar = e?.error?.msg || 'No se pudo habilitar la dirección. Intenta de nuevo.';
    } finally {
      this.habilitando = false;
    }
  };

  abrirCambioAcronimo(): void {
    this.nuevoAcronimo = ''; this.estadoNuevo = { fase: 'vacio', mensaje: '' }; this.cambioAbierto = true;
  }

  revisarNuevoAcronimo(): void {
    clearTimeout(this.timerNuevo);
    const pedido = ++this.pedidoNuevo;
    const v = String(this.nuevoAcronimo || '').toLowerCase().trim();
    this.nuevoAcronimo = v;
    if (!v) { this.estadoNuevo = { fase: 'vacio', mensaje: '' }; return; }
    if (!acronimoConFormato(v)) {
      this.estadoNuevo = { fase: 'mal', mensaje: 'Sólo minúsculas, números y guiones; no puede empezar ni terminar con guion.' }; return;
    }
    if (v === this.comercio?.portal?.acronimo) { this.estadoNuevo = { fase: 'mal', mensaje: 'Es el acrónimo que ya tiene.' }; return; }
    this.estadoNuevo = { fase: 'revisando', mensaje: '' };
    this.timerNuevo = setTimeout(async () => {
      try {
        const r = await this._merchant.acronimoDisponible(v, this.codigo);
        if (pedido !== this.pedidoNuevo) return;
        this.estadoNuevo = r.libre ? { fase: 'ok', mensaje: '' } : {
          fase: 'mal',
          mensaje: r.motivo === 'reservado' ? 'Esa palabra está reservada por el sistema. Elige otra.'
            : r.motivo === 'ocupado' ? `Ya la usa otro comercio. Prueba con ${v}-2.`
              : 'Sólo minúsculas, números y guiones; no puede empezar ni terminar con guion.',
        };
      } catch {
        if (pedido === this.pedidoNuevo) this.estadoNuevo = { fase: 'mal', mensaje: 'No se pudo revisar si está libre. Vuelve a intentarlo.' };
      }
    }, 350);
  }

  cambiarAcronimo = async (): Promise<void> => {
    if (this.estadoNuevo.fase !== 'ok' || this.cambiandoAcronimo) return;
    this.cambiandoAcronimo = true;
    try {
      const r = await this._merchant.cambiarAcronimo(this.codigo, this.nuevoAcronimo);
      if (r?.portal && this.comercio) this.comercio = { ...this.comercio, portal: r.portal };
      this.cambioAbierto = false;
      Swal.fire({ toast: true, position: 'top-end', timer: 2200, showConfirmButton: false, icon: 'success', title: 'Acrónimo cambiado' });
    } catch (e: any) {
      Swal.fire({ icon: 'error', title: 'No se pudo cambiar el acrónimo', text: e?.error?.msg || 'Intenta de nuevo.' });
    } finally {
      this.cambiandoAcronimo = false;
    }
  };
}
